import { useEffect, useState } from 'react';
import { Box, HStack } from '@chakra-ui/react';
import type { Editor } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/react';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
} from 'lucide-react';
import { FormatButton } from './FormatButton';

export function DefaultBubbleMenu({ editor }: { editor: Editor | null }) {
  const [showBubbleMenu, setShowBubbleMenu] = useState(true);

  useEffect(() => {
    const updateBubbleMenu = () => {
      if (editor) {
        const isMediaOrTableActive = [
          'image-dialog',
          'image',
          'video-dialog',
          'video',
          'youtube',
          'table',
        ].some((type) => editor.isActive(type));
        setShowBubbleMenu(!isMediaOrTableActive);
      }
    };

    if (editor) {
      editor.on('selectionUpdate', updateBubbleMenu);
    }

    return () => {
      if (editor) {
        editor.off('selectionUpdate', updateBubbleMenu);
      }
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        placement: 'top-start',
        offset: [0, 10],
      }}
      className={showBubbleMenu ? 'display-block' : 'hidden'}
    >
      <HStack
        gap={0.5}
        bg="bg.surface"
        shadow="sm"
        outline="1px"
        p={0.5}
        borderRadius="md"
        borderWidth="1px"
        boxShadow="sm"
      >
        <FormatButton
          label="bold"
          icon={Bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
        />
        <FormatButton
          label="italic"
          icon={Italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
        />
        <FormatButton
          label="strike"
          icon={Strikethrough}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
        />
        <Box bgColor="border.default" h="32px" w="2px" />
        <FormatButton
          label="large heading"
          aria-label="large heading"
          icon={Heading1Icon}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
        />
        <FormatButton
          label="medium heading"
          aria-label="medium heading"
          icon={Heading2Icon}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
        />
        <FormatButton
          label="small heading"
          aria-label="small heading"
          icon={Heading3Icon}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
        />
        {/* Text alignment causes some unexpected UX behaviour in the editor.
         * It's better to remove it for now and re-think the UX for text alignment.
         */}
        {/* <Box bgColor="border.default" h="32px" w="2px" />
        <FormatButton
          label="align left"
          aria-label="align left"
          icon={AlignLeft}
          onClick={() => editor.commands.setTextAlign('left')}
          isActive={editor.isActive({ textAlign: 'left' })}
        />
        <FormatButton
          label="align center"
          aria-label="align center"
          icon={AlignCenter}
          onClick={() => editor.commands.setTextAlign('center')}
          isActive={editor.isActive({ textAlign: 'center' })}
        />
        <FormatButton
          label="align right"
          aria-label="align right"
          icon={AlignRight}
          onClick={() => editor.commands.setTextAlign('right')}
          isActive={editor.isActive({ textAlign: 'right' })}
        /> */}
      </HStack>
    </BubbleMenu>
  );
}
